<script lang="ts" setup>
import type { IListFlaggedContentFilters } from "@/api"
import type { FlaggedContent } from "@/models"
import { computed, useTemplateRef, watch } from "vue"
import { useListFlaggedContent } from "@/api"
import { FlaggedContentData, FlaggedContentFilters } from "@/components"
import { Badge, Button, NoData, Pagination } from "@/components/ui"
import { useConvertedRouteParam, useFilters, useModal, usePagination, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { FlaggedContentFiltersDrawerKey } from "@/symbols"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/reported-content", value => Number(value))

const { t } = useTranslation()

const contentWrapperRef = useTemplateRef<HTMLElement | null>("contentWrapperRef")

const { filters, count: filterCount } = useFilters<IListFlaggedContentFilters>({
  page: 1,
  perPage: 25,
  status: undefined,
  contentType: undefined,
})

const { data: flaggedContentResult, isLoading, isRefetching, pageCount } = useListFlaggedContent(eventVaultId, filters)

const flaggedContent = computed<FlaggedContent[]>(() => flaggedContentResult.value?.data ?? [])

const pagination = usePagination({
  initialPerPage: 25,
  pageCount,
  scrollStartRef: contentWrapperRef,
})

const { toggle: toggleFilters } = useModal(FlaggedContentFiltersDrawerKey)

watch(
  () => [pagination.page.value, pagination.perPage.value],
  ([page, perPage]) => {
    filters.page = page as number
    filters.perPage = perPage as number
  },
)

watch(
  () => filters.perPage,
  (value) => {
    pagination.perPage.value = value ?? 25
  },
)

function onFiltersChanged() {
  pagination.page.value = 1
  filters.page = 1
}
</script>

<template>
  <SidebarHostEventLayout>
    <div ref="contentWrapperRef" class="mb-8">
      <div class="flex flex-col gap-2">
        <h1 class="font-comfortaa font-bold text-xl mb-2">
          {{ t("reported_content_page.title") }}
        </h1>
        <p class="text-label text-sm md:text-base font-montserrat max-w-3xl">
          {{ t("reported_content_page.description") }}
        </p>
      </div>
    </div>

    <div class="mb-4 flex justify-end">
      <Badge :count="filterCount">
        <Button
          data-test="reported-content-filter-button"
          prefix-icon="Filter"
          variant="tertiary"
          @click="toggleFilters"
        >
          {{ t("reported_content_page.filter_btn") }}
        </Button>
      </Badge>
    </div>

    <div v-if="isLoading || isRefetching" class="flex flex-col gap-4" data-test="reported-content-loading">
      <div
        v-for="n in 3"
        :key="`skeleton-${n}`"
        class="overflow-hidden p-2 md:p-4 border rounded-xl shadow-xs"
      >
        <div class="flex flex-col gap-3">
          <div class="rounded-lg overflow-hidden">
            <div class="gpx-skeleton w-full h-8" />
            <div class="gpx-skeleton w-full h-32 md:h-40 rounded-none mt-2" />
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <div class="gpx-skeleton h-6 w-24 rounded-full" />
            <div class="gpx-skeleton h-10 w-28" />
            <div class="gpx-skeleton h-10 w-28" />
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="flaggedContent?.length" class="flex flex-col gap-4" data-test="flagged-content-list">
      <FlaggedContentData
        v-for="content in flaggedContent"
        :key="`${content.contentType}-${content.contentId}`"
        :content="content"
      />

      <Pagination
        :current-page="pagination.page.value"
        :page-count="pagination.pageCount?.value"
        :per-page="pagination.perPage.value"
        :can-next="pagination.canNext.value"
        :can-previous="pagination.canPrevious.value"
        data-test="reported-content-pagination"
        class="mt-8"
        @next="pagination.next"
        @previous="pagination.previous"
      />
    </div>

    <NoData
      v-else
      hide-button
      :headline="t('reported_content_page.no_data_headline')"
      :content="t('reported_content_page.no_data_content')"
    />

    <FlaggedContentFilters
      v-model:status="filters.status"
      v-model:content-type="filters.contentType"
      v-model:per-page="filters.perPage"
      data-test="flagged-content-filters"
      @filters-changed="onFiltersChanged"
    />
  </SidebarHostEventLayout>
</template>
